import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AccessNotice, Button, Checkbox, Dialog, PatientConfirmLine, Segmented, type Patient as DsPatient } from '@etnas/design-system';
import { BREAK_GLASS_SCOPES, breakGlassApi, isApiError, session, tasksApi, type AccessCheck, type BreakGlassGrant, type BreakGlassReason, type BreakGlassScope } from '@/api';
import { Modal } from './Modal';
import { ActionError } from './States';
import { useToast } from './Toaster';
import { qk } from '@/lib/queryKeys';
import { fmtTime } from '@/lib/format';

/** Sent as reason codes (EMERGENCY_CARE, COVERING_CLINICIAN, IMMEDIATE_SAFETY_RISK) by breakGlassApi.start. */
const REASONS: [BreakGlassReason, string][] = [
  ['emergency', 'Emergency treatment, patient cannot consent'],
  ['covering', 'Covering clinician, care team unreachable'],
  ['safety', 'Immediate safety risk to patient or others'],
];

/**
 * Break-glass (BRK-001), functional version of the DS BreakGlassDialog: repeat the patient, a coded reason, what is
 * needed (the grant scope), what stays blocked, duration, acknowledgement — then step-up (triggered by the API's
 * ETNAS_STEP_UP_REQUIRED) and a time-boxed grant under the EMERGENCY purpose of use.
 */
export function BreakGlassDialog({ patient, patientId, slot = 1, onClose }: { patient: DsPatient; patientId: string; slot?: 1 | 2 | 3 | 4; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState<BreakGlassReason>('emergency');
  const [mins, setMins] = useState<'15' | '30' | '60'>('30');
  const [ack, setAck] = useState(false);
  const [scope, setScope] = useState<BreakGlassScope[]>(['patient.read', 'note.read', 'medication.read', 'allergy.read', 'result.read']);
  const ready = ack && scope.length > 0;
  const start = useMutation({
    mutationFn: () => breakGlassApi.start({ patientId, reason, scope, durationMinutes: Number(mins), warningAcknowledged: true }, patient.legalName),
    onSuccess: (g) => {
      // Reads under the grant must carry the EMERGENCY purpose of use (the chart keeps it while the grant is active).
      session.setContext({ purpose: 'EMERGENCY' });
      qc.invalidateQueries({ queryKey: qk.breakGlassActive });
      qc.invalidateQueries({ queryKey: qk.patient(patientId) });
      qc.invalidateQueries({ queryKey: qk.access(patientId) });
      toast.success('Emergency access is on', `Ends ${fmtTime(g.expiresAt)}. Every view is recorded and your privacy officer was notified.`);
      onClose();
    },
  });
  const stepUpCancelled = isApiError(start.error, 'ETNAS_STEP_UP_REQUIRED');
  return (
    <Modal onClose={start.isPending ? () => {} : onClose}>
      <Dialog
        title="Break glass for emergency access"
        eyebrow="Emergency access"
        tone="critical"
        onClose={onClose}
        width={620}
        footer={
          <>
            <span className="ex-small ex-fg-muted">Your privacy officer is notified immediately.</span>
            <span className="ex-row-gap">
              <Button variant="quiet" onClick={onClose} disabled={start.isPending}>
                Cancel
              </Button>
              <Button variant="danger" icon="glass" requires="step-up" disabled={!ready} loading={start.isPending} onClick={() => start.mutate()}>
                Verify and open for {mins} min
              </Button>
            </span>
          </>
        }
      >
        <PatientConfirmLine patient={patient} slot={slot} />
        <div className="ex-bg-grid">
          <fieldset className="ex-fieldset">
            <legend className="ex-lbl">Reason</legend>
            <div className="ex-radio-list" role="radiogroup" aria-label="Reason">
              {REASONS.map(([id, l]) => (
                <label key={id} className={'ex-radio' + (reason === id ? ' is-on' : '')}>
                  <input type="radio" name="bg-reason" checked={reason === id} onChange={() => setReason(id)} />
                  <span className="ex-radio-dot" />
                  {l}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="ex-fieldset">
            <legend className="ex-lbl">
              What you need<span className="ex-req"> *</span>
            </legend>
            <div className="ex-stack is-tight">
              {BREAK_GLASS_SCOPES.map((s) => (
                <Checkbox
                  key={s.id}
                  checked={scope.includes(s.id)}
                  disabled={s.id === 'patient.read'}
                  onChange={(on) => setScope(on ? [...scope, s.id] : scope.filter((x) => x !== s.id))}
                  label={s.id === 'patient.read' ? `${s.label} (always included)` : s.label}
                />
              ))}
            </div>
            <div className="ex-field-note ex-fg-muted">Ask only for what this emergency needs. Your privacy officer reviews the reason, the scope and every view.</div>
          </fieldset>
          <div className="ex-bg-scope">
            <div>
              <div className="ex-lbl">Becomes visible</div>
              <ul className="ex-ticks">
                {BREAK_GLASS_SCOPES.filter((s) => scope.includes(s.id)).map((s) => (
                  <li key={s.id}>{s.label}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="ex-lbl">Stays blocked</div>
              <ul className="ex-ticks is-no">
                <li>Records under a legal prohibition or a patient consent denial</li>
                <li>Psychotherapy notes</li>
              </ul>
            </div>
          </div>
          <div className="ex-row-gap ex-wrap">
            <span className="ex-lbl">Access lasts</span>
            <Segmented label="Duration" items={[{ id: '15', label: '15 min' }, { id: '30', label: '30 min' }, { id: '60', label: '60 min' }]} value={mins} onChange={(v) => setMins(v as '15' | '30' | '60')} />
          </div>
          <Checkbox checked={ack} onChange={setAck} label="I understand every view is recorded and reviewed, and misuse is a policy violation." />
        </div>
        {stepUpCancelled ? <p className="ex-small ex-fg-muted" style={{ margin: 0 }}>Identity check was cancelled. Nothing was opened.</p> : start.error ? <ActionError error={start.error} /> : null}
      </Dialog>
    </Modal>
  );
}

/**
 * Section-level restricted notice (guideline 30): no titles or dates; the count only when the server sends it.
 * Offers Request access and, when eligible, Break glass.
 */
export function RestrictedNotice({
  access, patient, patientId, count, compact, slot,
}: { access?: AccessCheck | null; patient: DsPatient; patientId: string; count?: number; compact?: boolean; slot?: 1 | 2 | 3 | 4 }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const req = useMutation({
    mutationFn: () =>
      tasksApi.create({ type: 'other', title: `Access request: restricted records for ${patient.legalName}`, description: 'Requested from the chart. Route to the care team or privacy office.', priority: 'action', patientId }),
    onSuccess: () => toast.success('Access requested', 'The care team and privacy office will review your request.'),
    onError: (e) => toast.error(e),
  });
  if (access?.activeBreakGlass) {
    return (
      <AccessNotice
        kind="break-glass"
        compact={compact}
        meta={`ends ${fmtTime(access.activeBreakGlass.expiresAt)}`}
        actions={<EndAccessButton grant={access.activeBreakGlass} patientId={patientId} />}
      />
    );
  }
  return (
    <>
      <AccessNotice
        kind="restricted"
        compact={compact}
        title={count ? `${count} item${count === 1 ? '' : 's'} restricted for you` : undefined}
        actions={
          <span className="ex-row-gap ex-wrap">
            <Button size="sm" variant="quiet" loading={req.isPending} onClick={() => req.mutate()}>
              Request access
            </Button>
            {access?.breakGlassEligible ? (
              <Button size="sm" variant="secondary" icon="glass" onClick={() => setOpen(true)}>
                Break glass
              </Button>
            ) : null}
          </span>
        }
      />
      {open ? <BreakGlassDialog patient={patient} patientId={patientId} slot={slot} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function EndAccessButton({ grant, patientId }: { grant: BreakGlassGrant; patientId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const end = useMutation({
    // If-Match: W/"<rowVersion>" of the grant (breakGlassApi.end looks it up when missing).
    mutationFn: () => breakGlassApi.end(grant),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.breakGlassActive });
      qc.invalidateQueries({ queryKey: qk.patient(patientId) });
      qc.invalidateQueries({ queryKey: qk.access(patientId) });
      toast.success('Emergency access ended');
    },
    onError: (e) => toast.error(e),
  });
  return (
    <Button size="sm" variant="danger" loading={end.isPending} onClick={() => end.mutate()}>
      End access
    </Button>
  );
}

/**
 * Whole-chart denial (GET /patients/{id} → 403). Shows the policy outcome without revealing what is restricted,
 * offers Request access, and Break glass when the access check says the caller is eligible.
 * Intended for ChartLayout's error branch: `isApiError(pq.error, 'ETNAS_FORBIDDEN') ? <ChartAccessDenied .../>`.
 */
export function ChartAccessDenied({ access, patientId, display, slot }: { access?: AccessCheck | null; patientId: string; display?: string; slot?: 1 | 2 | 3 | 4 }) {
  const patient: DsPatient = { initials: '?', legalName: display ?? 'Restricted patient', dob: '', age: '', mrn: '', identity: 'unverified' } as DsPatient;
  const legal = access?.reasons?.includes('LEGAL_PROHIBITION');
  return (
    <div className="ex-stack">
      <RestrictedNotice access={access} patient={patient} patientId={patientId} slot={slot} />
      {legal ? <p className="ex-small ex-fg-muted" style={{ margin: 0 }}>A legal prohibition applies. Emergency access cannot override it.</p> : null}
    </div>
  );
}
